import * as GamePlay from './gameplay';
import { RED_PIECES, BLUE_PIECES, ITEMS } from './constants';
import ALL_ITEMS from 'items/all_items';

export function resetHistory(scene) {
  scene.game.registry.set({
    history_index: -1,
    history_states: [],
    history_states_above_player: [],
    history_states_decor_layer: [],
    history_states_below_player: [],
    history_room_states: [],
    history_initial_piece_map: [],
    history_piece_maps: [],
    history_states_level: [],
    history_colors: [],
    hasKey: false,
    hasMap: false
  });
}

export function setLevelState(scene, flag, value) {
  const [historyIndex, states_level] = scene.game.registry.get([
    'history_index',
    'history_states_level'
  ]);
  states_level[historyIndex][flag] = value;
  scene.game.registry.set('history_states_level', states_level);
}

export function getLevelState(scene, flag) {
  const [historyIndex, states_level] = scene.game.registry.get([
    'history_index',
    'history_states_level'
  ]);
  return states_level[historyIndex][flag];
}

export function undoLastMove(scene, cb) {
  let [states_level, history_colors] = scene.game.registry.get([
    'history_states_level',
    'history_colors'
  ]);

  let next;
  for (let i = history_colors.length - 1; i > -1; i--) {
    if (history_colors[i] === 'blue') {
      next = i - 1;
      break;
    }
  }
  if (next > -1) {
    scene.game.registry.set('history_index', next);
    states_level.splice(next + 1, states_level.length - next);
    history_colors.splice(next + 1, history_colors.length - next);
    scene.game.registry.set('history_states_level', states_level);
    scene.game.registry.set('history_colors', history_colors);

    for (let i = 1; i < 5; i++) {
      applyMapState(
        scene,
        scene.map,
        next,
        null,
        null,
        true,
        scene.game.registry.get('history_piece_maps')[next],
        i
      );
    }
  }
  // Call the level data handler
  if (scene.levelData.afterUndo) {
    scene.levelData.afterUndo(scene);
  }
  if (cb) {
    cb();
  }
  // pieces = null;
  return null;
}

const layerIndexToHistoryStateName = {
  4: 'history_states',
  3: 'history_states_above_player',
  2: 'history_states_decor_layer',
  1: 'history_states_below_player'
};

export function applyMapState(
  scene,
  map,
  index,
  mapUnitState,
  ignoreActiveRooms,
  placeSprites,
  multiplayerSpriteIds,
  layer = 4
) {
  const mapLayer = map.layers[layer].tilemapLayer;
  const prevSelectedCoords =
    (scene.previousSelectedPiece && {
      x: scene.previousSelectedPiece.tile.x,
      y: scene.previousSelectedPiece.tile.y
    }) ||
    null;

  if (placeSprites && layer === 4) {
    scene.pieces.getChildren().forEach(child => {
      const container = child.parentContainer;
      recursivelyDestroyContainer(container);
    });
    map.forEachTile(
      tile => {
        tile.occupant = null;
      },
      scene,
      0,
      0,
      map.width,
      map.height,
      null,
      'logic_layer'
    );
    scene.pieces.clear(true, true);
  }

  const battleZone = (scene.inZone && scene.inZone.rect) || {
    x: 0,
    y: 0,
    w: map.width,
    h: map.length
  };
  mapLayer.fill(-1, battleZone.x, battleZone.y, battleZone.w, battleZone.h);
  const stateData =
    mapUnitState ||
    scene.game.registry.get(layerIndexToHistoryStateName[layer])[index];
  stateData.forEach((tile, idx) => {
    const x = idx % battleZone.w;
    const y = Math.floor(idx / battleZone.w);
    const pieceTile = mapLayer.putTileAt(
      tile,
      battleZone.x + x,
      battleZone.y + y
    );
    if (tile !== -1 && placeSprites && layer === 4) {
      const isPieceOrItem = RED_PIECES.concat(BLUE_PIECES, ITEMS).includes(
        tile
      );
      if (isPieceOrItem) {
        const layoverData = multiplayerSpriteIds.find(
          piece => piece.x === pieceTile.x && piece.y === pieceTile.y
        );
        const item =
          layoverData && layoverData.itemID
            ? ALL_ITEMS[layoverData.itemID]
            : null;
        const piece = GamePlay.addPieceSpriteToBoard(
          scene,
          pieceTile,
          item,
          tile
        );
        if (layoverData) {
          piece.id = layoverData.id;
          piece.hp = layoverData.hp;
          piece.ss = layoverData.ss;
          piece.main = layoverData.main;
          piece.itemID = layoverData.itemID;
          piece.itemCharges = layoverData.itemCharges;
        }
        scene.currentSelectedPiece = null;
        if (
          prevSelectedCoords &&
          x === prevSelectedCoords.x &&
          y === prevSelectedCoords.y
        ) {
          scene.previousSelectedPiece = piece;
        }
      }
    }
  });
  if (!ignoreActiveRooms && index !== null && scene.currentActiveRooms) {
    const roomStates = scene.game.registry.get('history_room_states');
    const roomState = index || index === 0 ? index : roomStates.length;
    const activeRooms = roomStates[roomState];
    scene.setCurrentActiveRooms(activeRooms);
  }
}

export const recursivelyDestroyContainer = (container, depth = 0) => {
  const spritesToDestroy = [];
  container.list.forEach(child => {
    if (child.type === 'Sprite' && depth > 0) {
      spritesToDestroy.push(child);
    }
    if (child.type === 'Container') {
      recursivelyDestroyContainer(child, depth + 1);
    }
  });
  spritesToDestroy.forEach(sprite => sprite.destroy());
};

export function saveMapState(scene, color, replace = false) {
  const data = GamePlay.getMapPieceData(scene.map);
  const data_below = GamePlay.getMapPieceData(scene.map, 1);
  const data_above = GamePlay.getMapPieceData(scene.map, 3);
  const data_decor = GamePlay.getMapPieceData(scene.map, 2);

  let [
    index,
    states,
    states_below,
    states_above,
    states_decor,
    room_states,
    history_piece_maps,
    states_level,
    history_colors
  ] = scene.game.registry.get([
    'history_index',
    'history_states',
    'history_states_below_player',
    'history_states_above_player',
    'history_states_decor_layer',
    'history_room_states',
    'history_piece_maps',
    'history_states_level',
    'history_colors'
  ]);
  index += replace ? 0 : 1;
  states[index] = data;
  states_above[index] = data_above;
  states_below[index] = data_below;
  states_decor[index] = data_decor;
  states_level[index] =
    index - 1 > -1
      ? JSON.parse(JSON.stringify(states_level[replace ? index : index - 1]))
      : {};
  history_colors[index] = color;

  scene.game.registry.set('history_index', index);
  scene.game.registry.set('history_states', states);
  scene.game.registry.set('history_states_below_player', states_below);
  scene.game.registry.set('history_states_above_player', states_above);
  scene.game.registry.set('history_states_decor_layer', states_decor);
  scene.game.registry.set('history_states_level', states_level);
  scene.game.registry.set('history_colors', history_colors);

  const pieceMap = scene.pieces.getChildren().map(p => ({
    x: p.tile.x,
    y: p.tile.y,
    id: p.id,
    hp: p.hp,
    ss: p.ss,
    main: p.main,
    itemID: p.itemID,
    itemCharges: p.itemCharges
  }));
  history_piece_maps[index] = pieceMap;
  if (index === 0) {
    scene.game.registry.set('history_initial_piece_map', pieceMap);
  }
  scene.game.registry.set('history_piece_maps', history_piece_maps);

  if (scene.currentActiveRooms && scene.updateActiveRooms) {
    scene.updateActiveRooms();
    room_states[index] = scene.currentActiveRooms.roomIndexes;
    scene.game.registry.set('history_room_states', room_states);
  }

  return;
}
